<template>
  <div>
    <div v-if="showPrimaryMarketSelection">
      <h4 v-show="showHeaders" class="text-center m-b-20 m-t-20">
        {{ $t('Choose your primary market') }}
      </h4>
      <div class="row m-b-20">
        <div :class="{ 'col-sm-offset-3': centerContent }" class="text-left col-sm-6">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p v-show="showHeaders" v-html="primaryMarketSubtitle" />
          <div
            v-for="(
              { name, currency, islaunched, isSelected }, marketAbbreviation
            ) in stateObjectToUse"
            :key="marketAbbreviation"
          >
            <div
              v-if="islaunched"
              :class="{ faded: !islaunched || (!allowChangingMarket && !isSelected) }"
              :title="
                !islaunched
                  ? `${$t('Coming soon')}!`
                  : !allowChangingMarket && !isSelected
                  ? $t('Cannot change market with open offers')
                  : ''
              "
              :data-toggle="!islaunched || !allowChangingMarket ? 'tooltip' : ''"
              class="radio"
            >
              <label>
                <input
                  v-model="primaryMarket"
                  :value="marketAbbreviation"
                  :disabled="!islaunched || !allowChangingMarket"
                  name="market"
                  type="radio"
                />
                <i class="input-helper" />
                &nbsp;
                {{ $t(name) }}
                <span class="c-gray">({{ currency }})</span>
              </label>
            </div>
          </div>
          <div
            v-if="showPrimaryMarketChangedWarning"
            id="market-switch-warning"
            style=""
            class="alert alert-warning small"
          >
            <strong>{{ $t('Note') }}:</strong>
            <span v-if="account.isFreelancer">
              {{
                $t(
                  'When you change your market, you will need to update your hourly rate to reflect the currency in the new market',
                )
              }}.
              <br />
            </span>
            <!-- eslint-disable vue/no-v-html -->
            <strong
              v-html="
                $t(
                  'After your market has changed, you will be redirected to the relevant site for the new market, <u>and will need to login again.</u>',
                )
              "
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </div>
      </div>
    </div>
    <div v-if="showAvailability">
      <h4 v-show="showHeaders" class="text-center m-b-20 m-t-20">
        {{ $t('Availability for other markets') }}
      </h4>
      <div class="row">
        <div
          :class="{
            'col-sm-offset-3 col-sm-7': centerContent,
            'col-sm-10': !centerContent,
          }"
          class="text-left"
        >
          <p v-show="showHeaders">
            {{ $t('Which other markets are you available for?') }}
          </p>

          <Market
            v-for="(
              { name, regions, isSelected, isOnsite, isRemote }, marketAbbreviation
            ) in stateObjectToUse"
            :key="marketAbbreviation"
            :mandatory="isPrimary(marketAbbreviation)"
            :title="name"
            :market-abbreviation="marketAbbreviation"
            :regions="regions"
            :is-selected="isSelected"
            :is-remote="isRemote"
            :is-onsite="isOnsite"
            @toggle-market="toggleMarket"
            @toggle-market-onsite="toggleMarketOnsite"
            @toggle-market-remote="toggleMarketRemote"
            @toggle-region="toggleRegion"
            @toggle-region-onsite="toggleRegionOnsite"
            @toggle-region-remote="toggleRegionRemote"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  UPDATE_USER_STATE,
  UPDATE_USER_MARKET,
  UPDATE_USER_MARKET_REGION,
} from '@/state/mutation-types'
import Market from '@/components/MarketSelectionMarket.vue'

export default {
  components: { Market },
  props: {
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    showPrimaryMarketSelection: { type: Boolean, default: true },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    showHeaders: { type: Boolean, default: true },
    centerContent: { type: Boolean, default: false },
    initialState: { type: Object, default: () => {} },
    useVuexStore: { type: Boolean, default: false },
    showAvailability: { type: Boolean, default: false },
    // eslint-disable-next-line vue/no-boolean-default -- Baseline eslint ignore please fix if working on this
    allowChangingMarket: { type: Boolean, default: true },
  },
  data() {
    return {
      primaryMarket: '',
      showPrimaryMarketChangedWarning: false,
      markets: {},
    }
  },
  computed: {
    stateObjectToUse() {
      return this.useVuexStore ? this.$store.state.user.markets : this.markets
    },
    primaryMarketSubtitle() {
      return this.account.isFreelancer
        ? `${$t('In which <em>primary market</em> do you offer your freelancing service')}?`
        : `${$t('In which market do you want to hire freelancers?')}`
    },
    slimMarketsObjectForApi() {
      return Object.values(this.markets)
        .filter((a) => a.isSelected && (a.isOnsite || a.isRemote))
        .map(({ marketKey, isOnsite, isRemote, regions }) => ({
          key: marketKey,
          isOnsite,
          isRemote,
          regions: regions
            .filter((a) => a.isSelected && (a.isOnsite || a.isRemote))
            .map((a) => ({
              localId: a.id,
              isOnsite: a.isOnsite,
              isRemote: a.isRemote,
            })),
        }))
    },
  },
  watch: {
    primaryMarket(newPrimaryMarket, previousPrimaryMarket) {
      //  Select the same market in 'availability for other markets list'
      //  but don't de-select it if changing primary market to something else
      if (previousPrimaryMarket && this.markets.hasOwnProperty(newPrimaryMarket)) {
        this.markets[newPrimaryMarket].isSelected = true
      }
      this.showPrimaryMarketChangedWarning = newPrimaryMarket !== this.account.market
    },
    slimMarketsObjectForApi: {
      handler(slimMarketsObjectForApi) {
        this.$emit('market-selection-changed', slimMarketsObjectForApi)
      },
      deep: true,
    },
  },
  created() {
    this.markets = this.useVuexStore ? this.$store.state.user.markets : this.initialState
    // useVuexStore essentially means we didnt pass in an initial state, and we should just pull
    // initial state from the vuex store.
    // If not, we commit our initial state prop into the vuex store
    if (!this.useVuexStore) {
      this.$store.commit(UPDATE_USER_STATE, {
        fieldName: 'markets',
        fieldValue: this.initialState,
      })
    }
    this.primaryMarket = this.account.market
  },
  methods: {
    isPrimary(marketAbbreviation) {
      return marketAbbreviation === this.primaryMarket
    },
    toggleMarket(market, enable) {
      this.markets[market].isSelected = enable
      this.$store.commit(UPDATE_USER_MARKET, {
        market,
        fieldName: 'isSelected',
        fieldValue: enable,
      })
    },
    toggleMarketOnsite(market, enable) {
      this.markets[market].isOnsite = enable
      this.$store.commit(UPDATE_USER_MARKET, {
        market,
        fieldName: 'isOnsite',
        fieldValue: enable,
      })
    },
    toggleMarketRemote(market, enable) {
      this.markets[market].isRemote = enable
      this.$store.commit(UPDATE_USER_MARKET, {
        market,
        fieldName: 'isRemote',
        fieldValue: enable,
      })
    },
    toggleRegion(market, region, enable) {
      this.markets[market].regions.find(
        // eslint-disable-next-line eqeqeq -- Baseline eslint ignore please fix if working on this
        (element) => element.id == parseInt(region, 10),
      ).isSelected = enable
      this.$store.commit(UPDATE_USER_MARKET_REGION, {
        market,
        region,
        fieldName: 'isSelected',
        fieldValue: enable,
      })
    },
    toggleRegionOnsite(market, region, enable) {
      // eslint-disable-next-line eqeqeq -- Baseline eslint ignore please fix if working on this
      this.markets[market].regions.find((element) => element.id == parseInt(region, 10)).isOnsite =
        enable
      this.$store.commit(UPDATE_USER_MARKET_REGION, {
        market,
        region,
        fieldName: 'isOnsite',
        fieldValue: enable,
      })
    },
    toggleRegionRemote(market, region, enable) {
      // eslint-disable-next-line eqeqeq -- Baseline eslint ignore please fix if working on this
      this.markets[market].regions.find((element) => element.id == parseInt(region, 10)).isRemote =
        enable
      this.$store.commit(UPDATE_USER_MARKET_REGION, {
        market,
        region,
        fieldName: 'isRemote',
        fieldValue: enable,
      })
    },
  },
}
</script>
